/**
 * Cloudinary delivery helper (iter40).
 *
 * The website previously served every product / hero / category image from
 *   GridFS via `${REACT_APP_BACKEND_URL}/api/media/<file_id>`.
 *
 * After admins migrate to Cloudinary, image fields contain either:
 *   - a full Cloudinary URL  (https://res.cloudinary.com/<cloud>/image/upload/...)
 *   - OR a legacy /api/media/<id> path
 *
 * `imageUrl(url, opts)` returns a delivery-ready URL:
 *   1) Cloudinary URLs are rewritten with `f_auto,q_auto[,w_<n>]` transforms.
 *   2) Legacy /api/media/* URLs are wrapped with Cloudinary's `image/fetch`
 *      mode so they ALSO get WebP/AVIF auto-format, lazy resize and
 *      Cloudinary's global CDN — zero migration required.
 *   3) Anything else is returned as-is (data: URIs, placeholders, etc.).
 *
 *   opts = { width?: number, height?: number, crop?: 'fill'|'fit', dpr?: 'auto' }
 *
 * The cloud_name is fetched once from /api/cloudinary/config on app boot
 * and cached in `window.__cld_cloud`.  When unavailable (offline / first
 * paint), the helper falls back to returning the absolute backend URL so
 * images keep working.
 */
const BACKEND = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

let _cloud = null;
let _cloudPromise = null;

export async function loadCloudinaryConfig() {
  if (_cloud !== null) return _cloud;
  if (_cloudPromise) return _cloudPromise;
  _cloudPromise = fetch(`${BACKEND}/api/cloudinary/config`)
    .then((r) => (r.ok ? r.json() : { enabled: false }))
    .then((j) => {
      _cloud = j?.enabled && j?.cloud_name ? j.cloud_name : "";
      window.__cld_cloud = _cloud;
      return _cloud;
    })
    .catch(() => {
      _cloud = "";
      return _cloud;
    });
  return _cloudPromise;
}

export function getCloudName() {
  return _cloud || window.__cld_cloud || "";
}

function isCloudinaryUrl(u) {
  return typeof u === "string" && u.includes("res.cloudinary.com/");
}

function absolutiseLegacy(u) {
  if (!u) return u;
  if (u.startsWith("http://") || u.startsWith("https://")) return u;
  if (u.startsWith("/api/media/")) return BACKEND + u;
  return u;
}

function buildTransforms({ width, height, crop = "fill", dpr } = {}) {
  // iter92 — Cloudinary bill-per-transformation optimisation.
  //
  // Previously we always emitted `dpr_auto` which caused Cloudinary to
  // register a SEPARATE derivation per device-pixel-ratio (a 2× retina
  // phone counted as one impression, a 1× desktop counted as another
  // for the same visual size).  Removing dpr_auto by default:
  //   • cuts the number of derivations roughly in half
  //   • the source width we now request is already generous enough to
  //     look crisp on 2× displays because component `img` tags render
  //     at ~50-60% of the source width (e.g. 600 px source → ~300 CSS px
  //     card → 2× on retina fills at exactly 600).
  // Callers can still opt-in via `{ dpr: 'auto' }` when needed.
  const parts = ["f_auto", "q_auto"];
  if (dpr) parts.push(`dpr_${dpr}`);
  if (width) parts.push(`w_${Math.round(width)}`);
  if (height) parts.push(`h_${Math.round(height)}`);
  if (width || height) parts.push(`c_${crop}`);
  return parts.join(",");
}

export function imageUrl(rawUrl, opts = {}) {
  if (!rawUrl) return rawUrl;
  const cloud = getCloudName();

  // 1) Already a Cloudinary upload URL → inject (or upgrade) transforms.
  if (isCloudinaryUrl(rawUrl)) {
    // Cloudinary URLs look like:
    //   https://res.cloudinary.com/<cloud>/image/upload/[<transforms>/]v<n>/<public_id>.<ext>
    const t = buildTransforms(opts);
    return rawUrl.replace(
      /\/image\/upload\/(?:[^/]+\/)?(v\d+\/)?/,
      (_m, ver) => `/image/upload/${t}/${ver || ""}`,
    );
  }

  // 2) Legacy GridFS or absolute backend URL → route via Cloudinary fetch.
  const absolute = absolutiseLegacy(rawUrl);
  if (cloud && (absolute.startsWith("http://") || absolute.startsWith("https://"))) {
    const t = buildTransforms(opts);
    return `https://res.cloudinary.com/${cloud}/image/fetch/${t}/${encodeURIComponent(absolute)}`;
  }

  // 3) Fall through unchanged (e.g. data: URI, placeholder).
  return absolute || rawUrl;
}

/** Convenience helper: build the public delivery URL from a public_id
 *  returned by the admin picker. */
export function urlFromPublicId(publicId, { format = "jpg", ...opts } = {}) {
  const cloud = getCloudName();
  if (!cloud || !publicId) return "";
  const t = buildTransforms(opts);
  return `https://res.cloudinary.com/${cloud}/image/upload/${t}/${publicId}.${format}`;
}
